// Shared metric calculators for the per-sample NER bar chart used by both
// the Evaluation page (``EntityExtractionsTable``) and the Playground overview
// table (previously ``PlaygroundExamplesTable``). Both surfaces consume status arrays
// produced by the backend's ``batch_get_teacher_entity_statuses`` and need the
// same {successful, partial, failed, countItemsByStatus} shape to feed
// ``EntityStatusBarChart``.
//
// ``StatusType`` lives here (not next to the bar chart) because it is also
// imported by ``TaggedSample`` and ``EntityModal``. ``EntityExtractionsTable``
// re-exports it so existing imports keep working without churn.

export const StatusType = {
    WRONG_LOCATION: 'wrong_location',
    PARTIAL: 'partial',
    ERROR: 'error',
    SUCCESS: 'success',
    MISSED: 'missed',
};

// Convert a per-entity status array (output of the backend's
// ``batch_get_teacher_entity_statuses``) into the counters and per-label
// breakdown the bar chart and its tooltip popover render. Counts ``missed``
// alongside ``error`` because both are "failed" outcomes from the user's
// perspective, but also keeps a per-label ``failedBreakdown`` so the tooltip
// can split them into "Incorrectly detected" vs "Missed" — the detail view
// (``TaggedSample``) already renders the two outcomes differently (red vs
// gray + "this entity was missed by …"), so without the split the bar-chart
// tooltip and the detail view contradict each other on missed entities.
export const calculateSampleMetrics = (statuses) => {
    let successful = 0;
    let partial = 0;
    let error = 0;
    const countItemsByStatus = {
        success: [],
        partial: [],
        error: [],
        failedBreakdown: {
            missed: [],
            incorrectly_detected: [],
        },
    };

    const inc = (bucket, label) => {
        const existing = bucket.find((item) => item.label === label);
        if (existing) {
            existing.count += 1;
        } else {
            bucket.push({ count: 1, label });
        }
    };

    (statuses || []).forEach((status) => {
        switch (status?.status) {
            case 'success':
                successful += 1;
                inc(countItemsByStatus.success, status.expected_label);
                break;
            case 'partial':
                partial += 1;
                inc(countItemsByStatus.partial, status.expected_label);
                break;
            case 'missed': {
                error += 1;
                const labelToUse = status.expected_label || status.predicted_label;
                inc(countItemsByStatus.error, labelToUse);
                inc(countItemsByStatus.failedBreakdown.missed, labelToUse);
                break;
            }
            case 'error': {
                error += 1;
                const labelToUse = status.predicted_label || status.expected_label;
                inc(countItemsByStatus.error, labelToUse);
                inc(countItemsByStatus.failedBreakdown.incorrectly_detected, labelToUse);
                break;
            }
            default:
                error += 1;
                break;
        }
    });

    return { successful, partial, failed: error, countItemsByStatus };
};

// Relations are evaluated by comparing pairs of (source, target) entities
// between predictions and ground truth, treating ``other`` as "no relation".
// Mirrors the logic that previously lived in ``EntityExtractionsTable``; kept
// verbatim so the relations bar chart on the Evaluation page is unchanged.
export const calculateRelationMetrics = (sample, isNewModel) => {
    const predictedResponse = isNewModel
        ? sample?.new_model_response
        : sample?.teacher_response || sample?.comparison_model_response;
    const predictedRelations = (predictedResponse?.relations || []).map((r) => ({
        ...r,
        label: (r?.label || '').toLowerCase(),
    }));
    const goldRelations = (sample?.test_response?.relations || []).map((r) => ({
        ...r,
        label: (r?.label || '').toLowerCase(),
    }));

    const getIndexToContent = (entities = []) =>
        new Map(
            entities.map((e) => [
                e?.start_index,
                String(e?.content ?? '').toLowerCase(),
            ])
        );
    const goldIndexToContent = getIndexToContent(sample?.test_response?.entities || []);
    const predIndexToContent = getIndexToContent(predictedResponse?.entities || []);

    const toContentPairKeyFromIndices = (srcIdx, tgtIdx, indexToContent) => {
        const a = String(indexToContent.get(srcIdx) ?? '').toLowerCase();
        const b = String(indexToContent.get(tgtIdx) ?? '').toLowerCase();
        return a <= b ? `${a}|${b}` : `${b}|${a}`;
    };

    const goldByContentPair = new Map();
    goldRelations.forEach((r) => {
        const key = toContentPairKeyFromIndices(
            r.source_start_index,
            r.target_start_index,
            goldIndexToContent
        );
        goldByContentPair.set(key, r.label);
    });

    const predByContentPair = new Map();
    predictedRelations.forEach((r) => {
        const key = toContentPairKeyFromIndices(
            r.source_start_index,
            r.target_start_index,
            predIndexToContent
        );
        predByContentPair.set(key, r.label);
    });

    let correct = 0;
    let fabricated = 0;
    let missed = 0;
    let miscategorized = 0;

    const countItemsByStatus = {
        success: [],
        partial: [],
        error: [],
        failedBreakdown: { fabricated: 0, missed: 0, miscategorized: 0 },
    };

    const inc = (bucket, label) => {
        const existing = bucket.find((i) => i.label === label);
        if (existing) existing.count += 1;
        else bucket.push({ count: 1, label });
    };

    predictedRelations.forEach((r) => {
        const key = toContentPairKeyFromIndices(
            r.source_start_index,
            r.target_start_index,
            predIndexToContent
        );
        const goldLabel = goldByContentPair.get(key) ?? 'other';
        if (r.label === 'other') {
            return;
        }
        if (goldLabel === 'other') {
            fabricated += 1;
            inc(countItemsByStatus.error, r.label);
            countItemsByStatus.failedBreakdown.fabricated += 1;
        } else if (goldLabel === r.label) {
            correct += 1;
            inc(countItemsByStatus.success, r.label);
        } else {
            miscategorized += 1;
            inc(countItemsByStatus.error, r.label);
            countItemsByStatus.failedBreakdown.miscategorized += 1;
        }
    });

    goldRelations.forEach((r) => {
        const key = toContentPairKeyFromIndices(
            r.source_start_index,
            r.target_start_index,
            goldIndexToContent
        );
        const predLabel = predByContentPair.get(key);
        if (r.label === 'other') {
            if (typeof predLabel === 'undefined' || predLabel === 'other') {
                correct += 1;
                inc(countItemsByStatus.success, 'other');
            }
        } else if (!predLabel || predLabel === 'other') {
            missed += 1;
            inc(countItemsByStatus.error, r.label);
            countItemsByStatus.failedBreakdown.missed += 1;
        }
    });

    return {
        successful: correct,
        partial: 0,
        failed: fabricated + miscategorized + missed,
        countItemsByStatus,
    };
};
